<template>
  <vs-popup class="ghs-label-print-popup" title="경고표지 출력" :active.sync="showPopup">
    <div class="flex justify-end mb-4">
      <vs-button color="primary" :disabled="!canPrint" @click="onClickPrint">출력하기</vs-button>
    </div>

    <table class="size-guide-table">
      <colgroup>
        <col width="60" />
        <col />
        <col />
        <col />
        <col />
      </colgroup>
      <thead>
        <tr>
          <th></th>
          <th>구분</th>
          <th>용량</th>
          <th>최소 넓이</th>
          <th>크기</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in sizeRows" :key="row.key">
          <td class="checkbox-cell"><vs-checkbox v-model="row.checked" class="custom-checkbox" /></td>
          <td>{{ row.label }}</td>
          <td>{{ row.capacity }}</td>
          <td>{{ row.minArea }}</td>
          <td>{{ row.paperLabel }}</td>
        </tr>
      </tbody>
    </table>

    <!-- 실제 인쇄용 컨텐츠 (화면에는 보이지 않고, 출력하기 클릭 시 document.body 로 복제되어 인쇄된다) -->
    <div ref="printRoot" style="display: none">
      <template v-for="group in printGroups">
        <!-- 작성페이지 인쇄(Print.vue)와 같은 구성. 크기는 배율이 아니라 용지(size-a5 / size-a4)별 스타일로 따로 잡는다. -->
        <div :key="group.key" class="ghs-print-page" :class="`size-${group.sizeKey}`" :style="{ width: `${group.widthMm}mm` }">
          <img src="@/assets/images/logo/dksteel_print_logo.svg" alt="DK Steel Print Logo" class="ghs-logo" />
          <h1 class="ghs-product">{{ group.item.제품명 || "-" }}</h1>

          <div v-if="getCheckedPictograms(group.item).length" class="warning-list" :style="getListStyle(group)">
            <div v-for="p in getCheckedPictograms(group.item)" :key="p.key" class="warning-item">
              <img :src="getPictogramImg(p.key)" :alt="p.label" :style="getPictogramStyle(group)" />
              <p class="pictogram-code">{{ p.label }}</p>
            </div>
          </div>

          <h2 v-if="group.item.신호어 && group.item.신호어 !== '없음'" class="ghs-signal" :class="group.item.신호어 === '위험' ? 'danger' : 'warning'">
            {{ group.item.신호어 === "위험" ? "위험(Danger)" : "경고(Warning)" }}
          </h2>

          <table class="ghs-table">
            <tr v-for="(row, i) in getPhrases(group.item.유해위험문구)" :key="`h_${i}`">
              <th v-if="i === 0" :rowSpan="getPhrases(group.item.유해위험문구).length">유해·위험문구</th>
              <td>{{ row.문구 || "-" }}</td>
            </tr>
          </table>

          <table class="ghs-table">
            <tr v-for="(row, i) in getPhrases(group.item.예방조치문구)" :key="`p_${i}`">
              <th v-if="i === 0" :rowSpan="getPhrases(group.item.예방조치문구).length">예방조치문구</th>
              <td>{{ row.문구 || "-" }}</td>
            </tr>
          </table>

          <table v-if="group.item.제조사 || group.item.제조사전화번호" class="ghs-table ghs-supplier">
            <tr>
              <th rowSpan="2">공급자 정보</th>
              <th class="sub">제조사(공급사)</th>
              <td>{{ group.item.제조사 || "-" }}</td>
            </tr>
            <tr>
              <th class="sub">전화번호</th>
              <td>{{ group.item.제조사전화번호 || "-" }}</td>
            </tr>
          </table>
        </div>
      </template>
    </div>
  </vs-popup>
</template>

<script>
import { GHS_PICTOGRAM_LIST, getPictogramImg } from "./ghsConstants";

// 용지별 그림문자 칸 크기(mm). contentWidthMm 는 용지 폭에서 좌우 여백(.ghs-print-page padding)을 뺀 값.
const PICTOGRAM_MAX_MM = {
  a5: { contentWidthMm: 136, maxMm: 40, paddingMm: 2 },
  a4: { contentWidthMm: 194, maxMm: 56, paddingMm: 3 },
};

export default {
  name: "PopupGHSLabelPrint",
  props: {
    value: { type: Boolean, default: false },
    items: { type: Array, default: () => [] },
  },
  data() {
    return {
      showPopup: this.value,
      sizeRows: [
        { key: "소", label: "소", capacity: "5L 이상 ~ 50L 미만", minArea: "90cm²", paperLabel: "A5(148*210)", sizeKey: "a5", widthMm: 148, heightMm: 210, checked: false },
        { key: "중", label: "중", capacity: "50L 이상 ~ 200L 미만", minArea: "180cm²", paperLabel: "A5(148*210)", sizeKey: "a5", widthMm: 148, heightMm: 210, checked: false },
        { key: "대", label: "대", capacity: "200L 이상 ~ 500L 미만", minArea: "300cm²", paperLabel: "A5(148*210)", sizeKey: "a5", widthMm: 148, heightMm: 210, checked: false },
        { key: "특대", label: "특대", capacity: "500L 이상", minArea: "450cm²", paperLabel: "A4(210*297)", sizeKey: "a4", widthMm: 210, heightMm: 297, checked: false },
      ],
    };
  },
  computed: {
    canPrint() {
      return this.items.length > 0 && this.sizeRows.some((row) => row.checked);
    },
    printGroups() {
      const groups = [];
      const checkedRows = this.sizeRows.filter((row) => row.checked);
      for (const row of checkedRows) {
        for (const item of this.items) {
          groups.push({
            key: `${row.key}_${item.postId}`,
            item,
            sizeKey: row.sizeKey,
            widthMm: row.widthMm,
            heightMm: row.heightMm,
          });
        }
      }
      return groups;
    },
  },
  watch: {
    value(value) {
      this.showPopup = value;
      if (value) {
        // 매번 새로 열릴 때는 이전 선택을 초기화한다.
        this.sizeRows.forEach((row) => {
          row.checked = false;
        });
      }
    },
    showPopup(value) {
      this.$emit("input", value);
    },
  },
  methods: {
    getPictogramImg,
    getCheckedPictograms(item) {
      const 그림문자 = (item && item.그림문자) || {};
      return GHS_PICTOGRAM_LIST.filter((p) => 그림문자[p.key]);
    },
    // 그림문자 배치: 4개까지 한 줄, 5개 이상은 두 줄로 나눈다. 칸 크기는 용지별 최대값(PICTOGRAM_MAX_MM) 안에서 폭에 맞춘다.
    getPictogramLayout(group) {
      const count = this.getCheckedPictograms(group.item).length;
      const cols = count <= 4 ? Math.max(count, 1) : Math.ceil(count / 2);
      const { contentWidthMm, maxMm } = PICTOGRAM_MAX_MM[group.sizeKey];
      return { cols, sizeMm: Math.min(maxMm, Math.floor(contentWidthMm / cols)) };
    },
    getListStyle(group) {
      const { cols, sizeMm } = this.getPictogramLayout(group);
      return { gridTemplateColumns: `repeat(${cols}, ${sizeMm}mm)` };
    },
    getPictogramStyle(group) {
      const { sizeMm } = this.getPictogramLayout(group);
      // 칸 안쪽 여백과 테두리(약 1mm)만큼 뺀 정사각형
      const imgMm = sizeMm - (PICTOGRAM_MAX_MM[group.sizeKey].paddingMm * 2) - 1;
      return { width: `${imgMm}mm`, height: `${imgMm}mm` };
    },
    // 문구가 없는 문서도 표 자체는 출력하고 '-' 한 행으로 표시 (Print.vue 와 동일)
    getPhrases(list) {
      return Array.isArray(list) && list.length ? list : [{ 문구: "-" }];
    },
    onClickPrint() {
      if (!this.canPrint) return;
      const source = this.$refs.printRoot;
      if (!source) return;

      const printRoot = source.cloneNode(true);
      printRoot.removeAttribute("style");
      printRoot.id = "ghs-label-print-root";
      document.body.appendChild(printRoot);
      document.body.classList.add("ghs-label-printing");

      const cleanup = () => {
        document.body.classList.remove("ghs-label-printing");
        printRoot.remove();
        window.removeEventListener("afterprint", cleanup);
      };
      window.addEventListener("afterprint", cleanup);

      this.$nextTick(() => window.print());
    },
  },
};
</script>

<style scoped lang="scss">
.ghs-label-print-popup::v-deep {
  .vs-popup {
    width: 90%;
    @media (min-width: 768px) {
      width: 640px;
    }
  }
}

.size-guide-table {
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    font-size: 14px;
    text-align: center;
    border: 1px solid #cdcdcd;
    padding: 12px 8px;
  }

  th {
    font-weight: 700;
    background-color: #f9f9f9;
  }

  .checkbox-cell {
    ::v-deep .vs-checkbox {
      margin: 0 auto;
    }
  }
}
</style>

<!-- 인쇄 전용 스타일. body 전체와 @page 를 다뤄야 해서 scoped 를 쓰지 않는다. -->
<style lang="scss">
body.ghs-label-printing > *:not(#ghs-label-print-root) {
  display: none !important;
}

#ghs-label-print-root {
  display: none;
}

body.ghs-label-printing #ghs-label-print-root {
  display: block;
}

@page ghsA5Page {
  size: 148mm 210mm;
  margin: 0;
}
@page ghsA4Page {
  size: 210mm 297mm;
  margin: 0;
}

// 용지별 치수. 그림문자 칸 크기는 스크립트의 PICTOGRAM_MAX_MM 에서 정한다(padding 과 함께 맞출 것).
@mixin ghs-label-size($pagePadding, $logoHeight, $productFont, $productPadding, $gap, $codeFont, $pictoPadding, $signalFont, $signalPadding, $tableFont, $thWidth, $subThWidth, $cellPadding) {
  padding: $pagePadding;

  .ghs-logo {
    height: $logoHeight;
    margin-bottom: $gap;
  }
  .ghs-product {
    font-size: $productFont;
    padding: $productPadding 2mm;
    margin-bottom: $gap;
  }
  .warning-list .warning-item {
    padding: $pictoPadding $pictoPadding 1mm;

    p {
      font-size: $codeFont;
      margin-top: 0.5mm;
    }
  }
  .ghs-signal {
    font-size: $signalFont;
    padding: $signalPadding 0;
    margin-top: $gap;
  }
  .ghs-table {
    margin-top: $gap;

    th,
    td {
      font-size: $tableFont;
      padding: $cellPadding;
    }
    th {
      width: $thWidth;
    }
    th.sub {
      width: $subThWidth;
    }
  }
}

.ghs-print-page {
  box-sizing: border-box;
  background: #fff;
  color: #000;
  page-break-after: always;
  break-after: page;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;

  &.size-a5 {
    page: ghsA5Page;
    @include ghs-label-size(6mm, 6mm, 20pt, 3mm, 3mm, 7pt, 2mm, 16pt, 1.5mm, 8pt, 24mm, 22mm, 1.2mm 1.5mm);
  }
  &.size-a4 {
    page: ghsA4Page;
    @include ghs-label-size(8mm, 9mm, 30pt, 4.5mm, 4.5mm, 10pt, 3mm, 24pt, 2.5mm, 11pt, 34mm, 30mm, 2mm 2.5mm);
  }

  &:last-child {
    page-break-after: auto;
    break-after: auto;
  }

  .ghs-logo {
    display: block;
    width: auto;
  }

  .ghs-product {
    margin: 0;
    font-weight: 700;
    text-align: center;
    line-height: 1.2;
    word-break: keep-all;
    border: 0.8mm solid #515151;
  }

  .warning-list {
    display: grid;
    justify-content: center;

    .warning-item {
      box-sizing: border-box;
      border: 0.3mm solid #757575;
      margin-right: -0.3mm;
      margin-bottom: -0.3mm;
      text-align: center;

      img {
        display: block;
        margin: 0 auto;
        object-fit: contain;
      }

      p {
        font-weight: 700;
        color: #3c3c3c;
        line-height: 1.2;
        margin-bottom: 0;
        white-space: nowrap;
      }
    }
  }

  .ghs-signal {
    margin: 0;
    font-weight: 700;
    text-align: center;
    line-height: 1.2;
    color: #fff;

    &.danger {
      background-color: #e42728;
    }
    &.warning {
      background-color: #ff8510;
    }
  }

  .ghs-table {
    width: 100%;
    border-collapse: collapse;
    page-break-inside: auto;

    tr {
      page-break-inside: avoid;
      break-inside: avoid;
    }

    th,
    td {
      border: 0.3mm solid #757575;
      text-align: center;
      vertical-align: middle;
      line-height: 1.35;
      word-break: keep-all;
      overflow-wrap: anywhere;
    }

    th {
      font-weight: 700;
      background: #ececee;
    }
  }
}
</style>
